/**
 * Plain JavaScript intent queue display
 * Shows pending player intents with sliding animation on processing
 */

import type { GameState, QueuedIntent, StateChangeDetail, TilePosition } from '../model/gamestate/GameState';
import type { EventHub } from '../events/EventHub';
import { SKILL_TARGETING_STATE_EVENT, type SkillTargetCandidate, type SkillTargetingState } from '../events/SkillTargetingEvents';
import { actorColorForKind, drawActorSprite } from '../renderer/ActorSprite';

const MAX_VISIBLE_ITEMS = 10;
const STATUS_FLASH_DURATION = 120; // ms
const SLIDE_ANIMATION_DURATION = 180; // ms

type ProcessingPhase = 'idle' | 'flash' | 'slide';
type DisplayQueuedIntent = QueuedIntent & {
    displayDirection?: number;
    queueKey: string;
};

function getIntentStructuralKey(intent: QueuedIntent): string {
    const type = intent.type.toLowerCase();
    if (type === 'turn') {
        const direction = 'direction' in intent ? intent.direction : 'unknown';
        return `${type}:${direction}`;
    }
    if (type === 'movetotile') {
        const position = 'position' in intent ? intent.position : null;
        return `${type}:${position?.x ?? 'x'},${position?.y ?? 'y'}`;
    }
    if (type === 'useskill') {
        const skillId = 'skillId' in intent ? intent.skillId : 'unknown';
        return `${type}:${skillId}`;
    }
    if (type === 'useitem') {
        const itemId = 'itemId' in intent ? intent.itemId : 'unknown';
        return `${type}:${itemId}`;
    }
    if (type === 'useitemontile') {
        const itemId = 'itemId' in intent ? intent.itemId : 'unknown';
        const position = 'targetPosition' in intent ? intent.targetPosition : ('position' in intent ? intent.position : null);
        return `${type}:${itemId}:${position?.x ?? 'x'},${position?.y ?? 'y'}`;
    }
    if (type === 'interact') {
        const position = 'position' in intent ? intent.position : null;
        return `${type}:${position?.x ?? 'x'},${position?.y ?? 'y'}`;
    }
    return type;
}

function decorateQueue(queue: ReadonlyArray<QueuedIntent>): DisplayQueuedIntent[] {
    const counts = new Map<string, number>();
    return queue.map((intent) => {
        const baseKey = intent.intentId ?? `struct:${getIntentStructuralKey(intent)}`;
        const occurrence = (counts.get(baseKey) ?? 0) + 1;
        counts.set(baseKey, occurrence);
        return {
            ...intent,
            queueKey: `${baseKey}#${occurrence}`,
        };
    });
}

function prepareDisplayQueue(queue: ReadonlyArray<QueuedIntent>, currentTick: number): DisplayQueuedIntent[] {
    return decorateQueue(
        normalizeQueueForDisplay(
            queue.filter((intent) => isUpcomingIntent(intent, currentTick))
        )
    );
}

function isItemIntent(
    intent: QueuedIntent | undefined
): intent is QueuedIntent & { itemId: string; position: TilePosition } {
    return Boolean(intent && 'itemId' in intent && 'position' in intent);
}

function actorColorForCandidate(candidate: SkillTargetCandidate): string {
    if (candidate.targetType !== 'entity') {
        return actorColorForKind('remotePlayer');
    }
    return actorColorForKind(candidate.kind === 'mob' ? 'mob' : (candidate.kind === 'self' ? 'self' : 'remotePlayer'));
}

function normalizeQueueForDisplay(queue: ReadonlyArray<QueuedIntent>): QueuedIntent[] {
    const normalized: QueuedIntent[] = [];

    const positionsEqual = (a: TilePosition | undefined, b: TilePosition | undefined): boolean => {
        if (!a || !b) return false;
        return a.x === b.x && a.y === b.y;
    };

    for (let i = 0; i < queue.length; i += 1) {
        const current = queue[i];
        const next = queue[i + 1];
        const currentType = current.type.toLowerCase();
        const nextType = next?.type?.toLowerCase();

        if (
            currentType === 'useitem' &&
            next &&
            nextType === 'useitemontile' &&
            isItemIntent(current) &&
            isItemIntent(next) &&
            current.itemId === next.itemId &&
            positionsEqual(current.position, next.position)
        ) {
            continue;
        }

        normalized.push(current);
    }

    return normalized;
}

function isUpcomingIntent(intent: QueuedIntent, currentTick: number): boolean {
    if (typeof intent.tickTarget !== 'number') {
        return true;
    }

    if (intent.validationStatus === 'valid' || intent.validationStatus === 'invalid') {
        if (typeof intent.resolvedTick !== 'number') {
            return true;
        }
        return intent.resolvedTick === currentTick;
    }

    return intent.tickTarget > currentTick;
}

function getArrowFromAngle(angle: number | undefined): string {
    if (angle === undefined) return '↑';
    const normalized = ((angle % 360) + 360) % 360;
    if (normalized >= 315 || normalized < 45) return '↑';
    if (normalized >= 45 && normalized < 135) return '→';
    if (normalized >= 135 && normalized < 225) return '↓';
    if (normalized >= 225 && normalized < 315) return '←';
    return '?';
}

export interface IntentQueueDisplayHandle {
    destroy(): void;
}

export function createIntentQueueDisplay(
    gameState: GameState,
    mountPoint: HTMLElement,
    eventHub: EventHub
): IntentQueueDisplayHandle {
    let sourceQueue: DisplayQueuedIntent[] = [];
    let visibleQueue: DisplayQueuedIntent[] = [];
    let processingIntent: DisplayQueuedIntent | null = null;
    let processingPhase: ProcessingPhase = 'idle';
    let flashTimer: ReturnType<typeof setTimeout> | null = null;
    let slideTimer: ReturnType<typeof setTimeout> | null = null;
    let suppressedIntentKeys = new Set<string>();
    let targetingState: SkillTargetingState = {
        active: false,
        skillId: null,
        requiredSlots: 0,
        slotTypes: [],
        hovered: null,
        selected: [],
    };

    const root = document.createElement('div');
    root.className = 'intent-queue-root';
    Object.assign(root.style, {
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        zIndex: '1000',
        pointerEvents: 'none',
    });

    const container = document.createElement('div');
    container.className = 'intent-queue-container';
    Object.assign(container.style, {
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(18, 22, 28, 0.9)',
        border: '1px solid #2a3142',
        borderRadius: '8px',
        padding: '8px 16px',
        gap: '12px',
        width: '344px',
        height: '50px',
        overflow: 'hidden',
    });

    const targetPanel = document.createElement('div');
    targetPanel.className = 'skill-target-panel';
    Object.assign(targetPanel.style, {
        display: 'none',
        alignItems: 'center',
        gap: '6px',
        minWidth: '50px',
        maxWidth: '320px',
        height: '50px',
        padding: '6px 8px',
        borderRadius: '8px',
        border: '1px solid #35516a',
        background: 'rgba(16, 27, 37, 0.92)',
    });

    const track = document.createElement('div');
    track.className = 'intent-queue-track';
    Object.assign(track.style, {
        display: 'flex',
        gap: '8px',
        willChange: 'transform',
    });

    container.appendChild(track);
    root.appendChild(container);
    root.appendChild(targetPanel);
    mountPoint.appendChild(root);

    function createTargetAvatar(candidate: SkillTargetCandidate): HTMLElement {
        if (candidate.targetType === 'tile') {
            const tileEl = document.createElement('div');
            Object.assign(tileEl.style, {
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                border: '1px solid rgba(191, 219, 254, 0.85)',
                background: 'rgba(30, 41, 59, 0.7)',
                fontSize: '9px',
                lineHeight: '1',
                textAlign: 'center',
                padding: '1px',
                boxSizing: 'border-box',
            });
            tileEl.textContent = `${candidate.position.x},${candidate.position.y}`;
            return tileEl;
        }

        const canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 24;
        Object.assign(canvas.style, {
            width: '24px',
            height: '24px',
            flexShrink: '0',
        });
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return canvas;
        }
        drawActorSprite(ctx, {
            centerX: 12,
            centerY: 12,
            size: 24,
            rotation: 180,
            color: actorColorForCandidate(candidate),
        });

        return canvas;
    }

    function renderTargetPanel(): void {
        if (!targetingState.active || targetingState.requiredSlots <= 0) {
            targetPanel.style.display = 'none';
            targetPanel.innerHTML = '';
            return;
        }

        targetPanel.style.display = 'flex';
        targetPanel.innerHTML = '';

        const firstEmptyIndex = targetingState.selected.findIndex((entry) => entry === null);

        for (let i = 0; i < targetingState.requiredSlots; i += 1) {
            const selected = targetingState.selected[i] ?? null;
            const preview = selected ?? (i === firstEmptyIndex ? targetingState.hovered : null);
            const isConfirmed = Boolean(selected);
            const isPreview = !isConfirmed && Boolean(preview);
            const slot = document.createElement('div');
            Object.assign(slot.style, {
                width: '38px',
                height: '38px',
                borderRadius: '6px',
                border: isConfirmed
                    ? '1px solid rgba(147, 197, 253, 0.95)'
                    : isPreview
                        ? '1px solid rgba(147, 197, 253, 0.5)'
                        : '1px solid rgba(147, 197, 253, 0.35)',
                background: isConfirmed
                    ? 'rgba(59, 130, 246, 0.24)'
                    : isPreview
                        ? 'rgba(59, 130, 246, 0.16)'
                        : 'rgba(30, 64, 175, 0.08)',
                color: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isConfirmed ? '1' : (isPreview ? '0.55' : '1'),
            });

            if (!preview) {
                targetPanel.appendChild(slot);
                continue;
            }

            slot.appendChild(createTargetAvatar(preview));
            targetPanel.appendChild(slot);
        }
    }

    function createItemElement(intent: DisplayQueuedIntent, index: number): HTMLDivElement {
        const item = document.createElement('div');
        item.className = 'intent-queue-item';

        const baseStyle: Record<string, string> = {
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#252c3a',
            borderRadius: '4px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#3a4354',
            fontSize: '1.2rem',
            color: '#e0e7ff',
            flexShrink: '0',
            position: 'relative',
        };

        const status = intent.validationStatus ?? 'pending';
        if (status === 'valid') {
            baseStyle.background = 'rgba(74, 222, 128, 0.2)';
            baseStyle.borderColor = 'rgba(74, 222, 128, 0.9)';
            baseStyle.color = '#4ade80';
        } else if (status === 'invalid') {
            baseStyle.background = 'rgba(220, 38, 38, 0.25)';
            baseStyle.borderColor = 'rgba(220, 38, 38, 0.8)';
            baseStyle.color = '#fca5a5';
        } else {
            baseStyle.background = 'rgba(250, 204, 21, 0.15)';
            baseStyle.borderColor = 'rgba(250, 204, 21, 0.7)';
            baseStyle.color = '#fef08a';
        }

        if (processingPhase === 'slide' && index === 0) {
            baseStyle.opacity = '0';
            baseStyle.transition = 'opacity 0.2s ease-out';
        }

        Object.assign(item.style, baseStyle);

        item.textContent = getIcon(intent, index);

        if (intent.type.toLowerCase() === 'useitemontile') {
            const indicator = document.createElement('div');
            Object.assign(indicator.style, {
                position: 'absolute',
                bottom: '-4px',
                right: '-4px',
                fontSize: '0.8rem',
            });
            indicator.textContent = '👆';
            item.appendChild(indicator);
        }

        return item;
    }

    function deriveMoveDirection(intentIndex: number): number | undefined {
        const displayItems = processingIntent
            ? [processingIntent, ...visibleQueue]
            : visibleQueue;
        const moveIntent = displayItems[intentIndex];
        if (!moveIntent || moveIntent.type.toLowerCase() !== 'movetotile' || !('position' in moveIntent)) {
            return undefined;
        }

        let cursor = gameState.getCharacterPosition();
        for (let i = 0; i < intentIndex; i += 1) {
            const intent = displayItems[i];
            const type = intent.type.toLowerCase();
            if (type === 'movetotile' && 'position' in intent) {
                cursor = intent.position;
            }
        }

        if (cursor.x === moveIntent.position.x && cursor.y === moveIntent.position.y) {
            return undefined;
        }

        const grid = gameState.getGrid();
        return grid.snapFacing(
            Math.atan2(moveIntent.position.y - cursor.y, moveIntent.position.x - cursor.x) * (180 / Math.PI) + 90
        );
    }

    function getIcon(intent: DisplayQueuedIntent, index: number): string {
        const type = intent.type.toLowerCase();

        switch (type) {
            case 'movetotile': {
                const direction = intent.displayDirection
                    ?? (('direction' in intent && typeof intent.direction === 'number')
                        ? intent.direction
                        : (deriveMoveDirection(index) ?? gameState.getCharacterRotation()));
                return getArrowFromAngle(direction);
            }
            case 'turn':
                return '\u21BB';
            case 'useskill':
                return '🔥';
            case 'useitem':
                return '🧪';
            case 'useitemontile':
                return '🎯';
            default:
                return '❓';
        }
    }

    function render(): void {
        track.innerHTML = '';

        const displayItems = processingIntent
            ? [processingIntent, ...visibleQueue]
            : visibleQueue;

        const visible = displayItems.slice(0, MAX_VISIBLE_ITEMS);

        visible.forEach((intent, index) => {
            track.appendChild(createItemElement(intent, index));
        });

        if (processingPhase === 'slide') {
            track.style.transform = 'translateX(-40px)';
            track.style.transition = `transform ${SLIDE_ANIMATION_DURATION}ms ease-out`;
        } else {
            track.style.transform = 'translateX(0)';
            track.style.transition = 'none';
        }

        renderTargetPanel();
    }

    function isResolved(intent: QueuedIntent | undefined): intent is QueuedIntent & { validationStatus: 'valid' | 'invalid' } {
        return intent?.validationStatus === 'valid' || intent?.validationStatus === 'invalid';
    }

    function syncSuppressedIntentKeys(): void {
        const sourceKeys = new Set(sourceQueue.map((intent) => intent.queueKey));
        suppressedIntentKeys = new Set(
            [...suppressedIntentKeys].filter((key) => sourceKeys.has(key))
        );
    }

    function computeVisibleQueue(): DisplayQueuedIntent[] {
        return sourceQueue
            .filter((intent) => !suppressedIntentKeys.has(intent.queueKey))
            .map((intent) => ({ ...intent }));
    }

    function clearTimers(): void {
        if (flashTimer) {
            clearTimeout(flashTimer);
            flashTimer = null;
        }
        if (slideTimer) {
            clearTimeout(slideTimer);
            slideTimer = null;
        }
    }

    function maybeProcessResolvedHead(): void {
        if (processingPhase !== 'idle') {
            return;
        }
        const nextHead = visibleQueue[0];
        if (!isResolved(nextHead)) {
            return;
        }

        const frozenDisplayDirection = nextHead.type.toLowerCase() === 'movetotile'
            ? (('direction' in nextHead && typeof nextHead.direction === 'number')
                ? nextHead.direction
                : deriveMoveDirection(0))
            : undefined;

        processingIntent = {
            ...nextHead,
            displayDirection: frozenDisplayDirection,
        };
        processingPhase = 'flash';
        suppressedIntentKeys.add(nextHead.queueKey);
        visibleQueue = computeVisibleQueue();
        render();

        flashTimer = setTimeout(() => {
            flashTimer = null;
            processingPhase = 'slide';
            render();

            slideTimer = setTimeout(() => {
                processingIntent = null;
                processingPhase = 'idle';
                slideTimer = null;
                visibleQueue = computeVisibleQueue();
                render();
                maybeProcessResolvedHead();
            }, SLIDE_ANIMATION_DURATION);
        }, STATUS_FLASH_DURATION);
    }

    function syncQueueFromGameState(): void {
        sourceQueue = prepareDisplayQueue(gameState.getPendingQueue(), gameState.getTick());
        syncSuppressedIntentKeys();
        visibleQueue = computeVisibleQueue();
        render();
        maybeProcessResolvedHead();
    }

    function handleStateChange(event: Event): void {
        const detail = (event as CustomEvent<StateChangeDetail>).detail;
        if (detail.type !== 'pendingQueueUpdated' && detail.type !== 'tickUpdated') {
            return;
        }
        syncQueueFromGameState();
    }

    gameState.addEventListener('stateChanged', handleStateChange);
    const handleTargetingState = (payload: SkillTargetingState | undefined): void => {
        targetingState = payload ?? {
            active: false,
            skillId: null,
            requiredSlots: 0,
            slotTypes: [],
            hovered: null,
            selected: [],
        };
        renderTargetPanel();
    };
    eventHub.on<SkillTargetingState>(SKILL_TARGETING_STATE_EVENT, handleTargetingState);

    syncQueueFromGameState();

    return {
        destroy() {
            gameState.removeEventListener('stateChanged', handleStateChange);
            eventHub.off<SkillTargetingState>(SKILL_TARGETING_STATE_EVENT, handleTargetingState);
            clearTimers();
            root.remove();
        },
    };
}

